<script setup lang="ts">
/**
 * ShareButton — "แชร์" chip that shares its card to the LINE chat the LIFF
 * app was opened from (bead lucky-war-trko).
 *
 * Renders ONLY inside the LIFF in-app browser (`liff.isInClient()`); the
 * normal browser PWA never sees it. The payload is a native Flex bubble built
 * from card data at click time (PO decision 2026-09-04 — no image capture).
 *
 * Context gating (useLiff.sendShareFlex): opened from a chat → the Flex lands
 * directly in that chat; opened from a rich menu / LINE HOME → LINE's share
 * target picker lets the user choose the destination.
 */
import { computed, onBeforeUnmount, ref } from 'vue';
import { useLiff } from '@/composables/useLiff';
import type { KpiCardShareInput } from '@/lib/shareFlex';

const props = defineProps<{
  /** Builds the share input at click time so it captures the card's CURRENT state. */
  build: () => KpiCardShareInput;
  /** Accessible name; defaults to the standard share label. */
  label?: string;
}>();

const { inClient, sendShareKpi, lastShareError, isSharePermissionError } = useLiff();

type Status = 'idle' | 'sending' | 'sent' | 'cancelled' | 'error';
const status = ref<Status>('idle');
let resetTimer: ReturnType<typeof setTimeout> | undefined;

onBeforeUnmount(() => {
  if (resetTimer) clearTimeout(resetTimer);
});

function flash(next: Status) {
  status.value = next;
  if (resetTimer) clearTimeout(resetTimer);
  // Errors stay visible longer so the reason can actually be read/relayed.
  resetTimer = setTimeout(() => {
    status.value = 'idle';
  }, next === 'error' ? 5000 : 2500);
}

async function share() {
  if (status.value === 'sending') return;
  status.value = 'sending';
  let input: KpiCardShareInput;
  try {
    input = props.build();
  } catch {
    flash('error');
    return;
  }
  const result = await sendShareKpi(input);
  if (result === 'sent' || result === 'shared') flash('sent');
  else if (result === 'cancelled') flash('cancelled');
  else flash('error');
}

const statusText = computed<Record<Status, string>>(() => ({
  idle: 'แชร์',
  sending: 'กำลังส่ง…',
  sent: 'ส่งแล้ว',
  cancelled: 'ยกเลิก',
  error: isSharePermissionError()
    ? 'ต้องอนุญาตสิทธิ์ส่งข้อความ'
    : `ส่งไม่สำเร็จ${lastShareError.value ? ` (${lastShareError.value.slice(0, 40)})` : ''}`,
}));
</script>

<template>
  <button
    v-if="inClient"
    type="button"
    class="share-btn"
    :class="{ 'share-btn--done': status === 'sent' }"
    :disabled="status === 'sending'"
    :aria-label="label ?? 'แชร์การ์ดไปยังแชท LINE'"
    @click="share"
  >
    <svg
      v-if="status === 'idle'"
      class="share-btn__icon"
      viewBox="0 0 16 16"
      width="12"
      height="12"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      stroke-width="1.6"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M10 2.5 13.5 6 10 9.5" />
      <path d="M13.5 6H6.5A3.5 3.5 0 0 0 3 9.5V13" />
    </svg>
    <span class="share-btn__text">{{ statusText[status] }}</span>
  </button>
</template>

<style scoped>
/* Chip treatment from the design system (surface chip + hairline + small
 * type) — sits quietly in card headers until needed. */
.share-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.22rem 0.55rem;
  border: 1px solid var(--line);
  border-radius: var(--radius-chip);
  background: var(--surface-3, var(--surface));
  color: var(--text-dim);
  font-family: var(--font-body);
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  flex-shrink: 0;
}

.share-btn:active:not(:disabled) {
  background: var(--surface-2, var(--surface));
}

.share-btn:disabled {
  opacity: 0.65;
  cursor: default;
}

.share-btn--done {
  color: var(--color-good, #1a7f37);
  border-color: var(--color-good, #1a7f37);
}

.share-btn__icon {
  flex-shrink: 0;
}
</style>
